import React, {
  Dispatch,
  FC,
  SetStateAction,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import { inRange } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InventoryData, ToggleViewState } from "pages/forecast/forecastTypes";
import { Paginator } from "primereact/paginator";
import { Dialog } from "primereact/dialog";
import HoverTable from "./HoverTable";
import { generateExcel, userPreferencesType } from "utils/generateSalesExcel";
import { capitalizeHeader, findObjectWithMaxKeys } from "utils/helper";
import {
  SalesData,
  salesFilterData,
} from "pages/sales-forecast/salesForecastTypes";

interface DataTablePropsType {
  data?: SalesData;
  first: number;
  setFirst: Dispatch<SetStateAction<number>>;
  setSalesFilter: Dispatch<SetStateAction<salesFilterData>>;
  salesFilterData: salesFilterData;
  downloadCsv: boolean;
  setDownloadCsv: Dispatch<SetStateAction<boolean>>;
  currentUIView: ToggleViewState;
}

function getObjectKeys(obj: any): string[] {
  // return Object.keys(obj).filter(key => key !== "history" && key !== "forecasts");
  return Object.keys(obj);
}

function combineKeysIntoObject(inputArray: any) {
  const combinedObject: any = {}; // Initialize an empty object

  inputArray.forEach((item: any) => {
    for (const key in item) {
      if (item.hasOwnProperty(key)) {
        combinedObject[key] = item[key]; // Assign key and value to the combined object
      }
    }
  });

  return combinedObject; // Return the combined object
}

const Grid: FC<DataTablePropsType> = memo(
  ({
    data,
    first,
    setFirst,
    setSalesFilter,
    downloadCsv,
    setDownloadCsv,
    salesFilterData,
    currentUIView,
  }) => {
    const [showHoverTable, setShowHoverTable] = useState(false);
    const rowIndexRef = useRef<number | null>(null);
    const rowDataRef = useRef();

    const userPreferences: userPreferencesType = {
      Forecast_by: salesFilterData.forecast_by,
      Start_date: salesFilterData.forecast_range_start_date,
      End_date: salesFilterData.forecast_range_end_date,
    };

    const filteredResult = data?.result.map((item) => {
      const { forecast, history, ...rest } = item;

      // Flatten the forecasts array into an object with date keys
      const forecastWithDates = forecast.reduce((acc: any, forecast: any) => {
        const dateKey = Object.keys(forecast)[0]; // Get the date string (e.g., "29-05 Oct '24")
        acc[dateKey] = forecast[dateKey]; // Set the date as a key in the new object
        return acc;
      }, {});

      // Merge the flattened forecasts with the rest of the object
      return { ...rest, ...forecastWithDates };
    });

    const historyData: any | [] = data?.result.map((item) => {
      const { history } = item;
      return history;
    });
	const forecastData: any | [] = data?.result.map((item) => {
		const { forecast } = item;
		return forecast;
	  });

    // const forecastData: any | [] = data?.result.map((item) => {
    //   const {
    //     history,
    //     product_id,
    //     product_name,
    //     category,
    //     total_forecasted_inventory,
    //     total_sales_quantity,
    //     ...rest
    //   } = item;
    //   return rest;
    // });
    let excelHeaders: string[] = [];
    let maxObject;

    if (data?.result && data.result.length > 0) {
      excelHeaders = getObjectKeys(data?.result[0]);
      maxObject = findObjectWithMaxKeys(filteredResult);
    }

    const excelData: any[] | undefined = data?.result.map((item: any) => {
      let tempObject: any = {};

      for (const key in item) {
        if (item.hasOwnProperty(key)) {
          if (excelHeaders.includes(key)) {
            tempObject[key] = item[key];
          }
          // else {

          // 	if (!tempObject.forecast) {
          // 		tempObject.forecast = {};
          // 	}
          // 	tempObject.forecast[key] = item[key];
          // }
        }
      }
      const { forecast, history } = tempObject;
      const newForecasts = combineKeysIntoObject(forecast);
      const newHistories = combineKeysIntoObject(history);
      tempObject = {
        ...tempObject,
        forecast: newForecasts,
        history: newHistories,
      };
      return tempObject;
    });

    useEffect(() => {
      if (downloadCsv && currentUIView.gridView) {
        generateExcel(userPreferences, excelData, excelHeaders);
        // exportPdf()
        setDownloadCsv(false);
      }
    }, [downloadCsv]);

    const clickColumnTemplate = (
      rowData: any,
      options: { rowIndex: number }
    ) => {
      const salesQtyKey = Object.keys(rowData).find((key) =>
        key.includes("sales_history")
      );

      return (
        <span
          onClick={() => {
            setShowHoverTable(true);
            rowIndexRef.current = options.rowIndex;
            rowDataRef.current = rowData;
          }}
          style={{ cursor: "pointer", textDecoration: "underline" }}
        >
          {rowData[salesQtyKey!]}
        </span>
      );
    };

    const headerTemplate = (col: string) => {
      const letter = capitalizeHeader(col);
      const firstWord = letter.split("(")[0];
      const secondWord = letter.split("y")[1]; //history's last y
      return (
        <div className="table-header">
          {firstWord} <br />
          {secondWord}
        </div>
      );
    };

    return (
      <>
        <Dialog
          visible={showHoverTable}
          className="hover-table"
          onHide={() => {
            if (!showHoverTable) return;
            setShowHoverTable(false);
          }}
          header="Sales History"
        >
          <HoverTable
            historyData={historyData[rowIndexRef.current!]}
            forecastData={forecastData[rowIndexRef.current!]}
            rowData={rowDataRef.current}
          />
        </Dialog>

        <div className="user-management-section">
          {data?.result && data?.result.length > 0 ? (
            <DataTable
              value={filteredResult ?? []}
              scrollable
              className="inventoryForecast salesinventoryForecast"
              scrollHeight="550px"
            >
              {filteredResult &&
                Object.keys(maxObject ?? {}).map((col, index) => {
                  if (col.includes("sales_history"))
                    return (
                      <Column
                        key={col}
                        frozen={inRange(index, 0, 5)}
                        field={col}
                        header={headerTemplate(col)}
                        body={clickColumnTemplate}
                      />
                    );
                  return (
                    <Column
                      key={col}
                      frozen={inRange(index, 0, 5)}
                      field={col}
                      header={capitalizeHeader(col)}
                    />
                  );
                })}
            </DataTable>
          ) : (
            <div className="custom-empty-message">
              No records available for this selection
            </div>
          )}
          {/* {data?.result.length! > 0 && <Paginator
				first={first}
				// rows={salesFilterData.size_limit} 
				rows={10} 
				// totalRecords={data?.total_count ?? 10}
				onPageChange={(e: any) => {
					setFirst(e.first)
					setSalesFilter((prev: any) => {
						return { ...prev, size_limit: e.rows, page: e.page + 1 }
					})

				}}
				className="p-paginator-bottom"
				rowsPerPageOptions={[25, 50, 100]}
			/>} */}
        </div>
      </>
    );
  }
);
export default Grid;
